import { motion } from 'framer-motion';
import { Eye, Github, Twitter, Mail, Linkedin, ExternalLink } from 'lucide-react';
import { AnimatedLogo } from './AnimatedLogo';

export function Footer() {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: <Github className="w-5 h-5" />, href: "https://github.com/cordalux", label: "GitHub" },
    { icon: <Twitter className="w-5 h-5" />, href: "https://twitter.com/cordalux", label: "Twitter" },
    { icon: <Linkedin className="w-5 h-5" />, href: "https://linkedin.com/company/cordalux", label: "LinkedIn" },
    { icon: <Mail className="w-5 h-5" />, href: "mailto:cordalux.co@gmail.com", label: "Email" },
  ];

  const footerSections = [
    {
      title: "Framework",
      links: [
        { label: "MindFork", href: "https://mindfork.me" },
        { label: "Glow", href: "#glow" },
        { label: "VANTA Core", href: "#vanta" },
      ]
    },
    {
      title: "Company",
      links: [
        { label: "About", href: "#about" },
        { label: "Projects", href: "#projects" },
        { label: "Technology", href: "#technology" },
      ]
    },
    {
      title: "Resources",
      links: [
        { label: "Contact", href: "#contact" },
        { label: "GitHub", href: "https://github.com/cordalux" },
        { label: "Email", href: "mailto:cordalux.co@gmail.com" },
      ]
    },
    {
      title: "Legal",
      links: [
        { label: "Privacy Policy", href: "#privacy" },
        { label: "Do Not Sell or Share", href: "https://cordalux.ai/mindfork/privacy" }
      ]
    }
  ];

  return (
    <footer className="border-t border-gray-200/20 dark:border-white/10 bg-black/5 dark:bg-white/5 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <motion.div
              className="flex items-center space-x-3 mb-6"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <AnimatedLogo size={40} animate={false} />
              <span className="font-['Spectral'] text-2xl tracking-wide">
                CORDALUX
              </span>
            </motion.div>

            <p className="font-['Inter'] text-gray-400 leading-relaxed mb-6 max-w-md">
              Recursive Light. Intelligent Design.
              <br />
              <br />
              Creating the future of AI through symbolic intelligence and conscious computation.
            </p>

            {/* Contact Email */}
            <div className="mb-6">
              <a
                href="mailto:cordalux.co@gmail.com"
                className="font-['Inter'] text-yellow-400 hover:text-yellow-300 transition-colors flex items-center space-x-2"
              >
                <Mail className="w-4 h-4" />
                <span>cordalux.co@gmail.com</span>
              </a>
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((link, index) => (
                <motion.a
                  key={index}
                  href={link.href}
                  target={link.href.startsWith('http') ? '_blank' : undefined}
                  rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="p-2 text-gray-400 hover:text-yellow-400 transition-colors"
                  whileHover={{ scale: 1.2, rotate: 360 }}
                  transition={{ duration: 0.3 }}
                  aria-label={link.label}
                >
                  {link.icon}
                </motion.a>
              ))}
            </div>
          </div>

          {/* Footer Links */}
          {footerSections.map((section, index) => (
            <div key={index}>
              <h3 className="font-['Spectral'] text-lg mb-4 text-yellow-400">
                {section.title}
              </h3>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <motion.a
                      href={link.href}
                      target={link.href.startsWith('http') ? '_blank' : undefined}
                      rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="font-['Inter'] text-gray-400 hover:text-white transition-colors flex items-center group"
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      {link.label}
                      {link.href.startsWith('http') && (
                        <ExternalLink className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </motion.a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-200/20 dark:border-white/10 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            {/* Copyright */}
            <div className="font-['Inter'] text-gray-400 text-sm">
              © {currentYear} Cordalux. All rights reserved in this reality and all parallel dimensions.
            </div>

            {/* Mystical Elements */}
            <div className="flex items-center space-x-6 text-gray-400 text-sm">
              <motion.span
                className="flex items-center space-x-2"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <div className="w-2 h-2 bg-yellow-400 rounded-full" />
                <span className="font-['Inter']">Core Status: Active</span>
              </motion.span>

              <span className="font-['Inter']">
                Built with recursive intelligence
              </span>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="relative mt-8">
          <motion.div
            className="absolute left-1/2 transform -translate-x-1/2 top-4"
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          >
            <Eye className="w-6 h-6 text-yellow-400/30" />
          </motion.div>
        </div>
      </div>
    </footer>
  );
}
